import type {
  GuidedAnswers,
  GuidedFollowUpAnswers,
  GuidedFollowUpId,
} from "@/features/blueprints/blueprintTypes";
import type { GuidedFollowUp } from "./guidedTypes";

export const guidedFollowUps: GuidedFollowUp[] = [
  {
    id: "booking",
    title: "Should users request a booking, choose from available slots, or both?",
    helper: "This helps define how much scheduling logic the first version needs.",
    type: "chips",
    options: ["Request only", "Choose from slots", "Both"],
  },
  {
    id: "aiOutput",
    title: "What should the AI generate?",
    helper: "Keep the AI output narrow enough for a strong first version.",
    type: "chips",
    options: [
      "Text/content",
      "Recommendations",
      "Summary",
      "Score or rating",
      "Plan or checklist",
    ],
  },
  {
    id: "privateData",
    title: "Does each user need private saved data?",
    helper: "This clarifies whether login is central to the product or only needed for an admin.",
    type: "chips",
    options: [
      "Yes - private data per user",
      "No - login is for admin only",
    ],
  },
  {
    id: "payments",
    title: "Should payments be in this first version or left for later?",
    helper: "Payments can add complexity, so this keeps the MVP scope intentional.",
    type: "chips",
    options: ["Include basic payment flow", "Leave for later"],
  },
  {
    id: "uploads",
    title: "What kind of upload?",
    helper: "This helps decide whether uploads belong in Version 1.",
    type: "chips",
    options: ["Images", "Documents", "CSV", "Simple text only is fine"],
  },
  {
    id: "userRoles",
    title: "How many user roles in the first version?",
    helper: "Keep roles simple unless they are essential to the first workflow.",
    type: "chips",
    options: ["Just one type", "User and admin", "Multiple roles"],
  },
  {
    id: "tracking",
    title: "What are the main things you want to track?",
    helper: "For example: guest name, RSVP status, payment status, food choice, arrival status.",
    type: "text",
    example: "Guest name, RSVP status, payment status, food choice, arrival status.",
  },
  {
    id: "appType",
    title: "Which type of app is this closest to?",
    helper: "This gives the blueprint a useful starting pattern when the idea is still broad.",
    type: "chips",
    options: [
      "Booking app",
      "Dashboard or tracker",
      "Form submission tool",
      "AI generator",
      "Directory or search tool",
      "CRM or lead tracker",
      "Member portal",
      "Marketplace",
    ],
  },
];

const triggerPriority = guidedFollowUps.map((followUp) => followUp.id);

function combinedText(answers: GuidedAnswers): string {
  return [
    answers.q1?.appIdea,
    answers.q2?.text,
    answers.q2?.chips?.join(" "),
    answers.q3?.chips?.join(" "),
    answers.q3?.extraText,
    answers.q4?.chips?.join(" "),
    answers.q5?.chips?.join(" "),
    answers.q5?.extraText,
    answers.extraDetail,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function hasPattern(value: string, pattern: RegExp): boolean {
  return pattern.test(value);
}

function isVagueIdea(answers: GuidedAnswers): boolean {
  const idea = answers.q1?.appIdea?.trim() ?? "";
  const wordCount = idea.split(/\s+/).filter(Boolean).length;

  if (wordCount < 8) {
    return true;
  }

  return !/(app|tool|portal|dashboard|tracker|booking|form|directory|crm|generator|marketplace|planner|manager)/i.test(
    idea,
  );
}

export function getTriggeredFollowUps(answers: GuidedAnswers): GuidedFollowUp[] {
  const text = combinedText(answers);
  const triggered = new Set<GuidedFollowUpId>();

  if (
    hasPattern(
      text,
      /\b(book|booking|bookings|appointment|appointments|schedule|scheduling|scheduled|slot|slots|availability|available times?)\b/,
    )
  ) {
    triggered.add("booking");
  }

  if (
    hasPattern(
      text,
      /\b(ai|artificial intelligence|generate an ai output|ai generation|generate|generator|generated|summary|summaries|recommendation|recommendations|score|rating|checklist|plan)\b/,
    )
  ) {
    triggered.add("aiOutput");
  }

  if (
    hasPattern(
      text,
      /\b(login|log in|signup|sign up|account|accounts|user accounts?|users|profiles?|private saved data|saved data|personal data)\b/,
    )
  ) {
    triggered.add("privateData");
  }

  if (
    hasPattern(
      text,
      /\b(payment|payments|checkout|subscription|subscriptions|invoice|invoices|paid|billing|charge|charges|payment processing)\b/,
    )
  ) {
    triggered.add("payments");
  }

  if (
    hasPattern(
      text,
      /\b(upload|uploads|uploaded|file|files|image|images|document|documents|csv|spreadsheet|attachment|attachments)\b/,
    )
  ) {
    triggered.add("uploads");
  }

  if (
    hasPattern(
      text,
      /\b(manage users|user management|role|roles|permission|permissions|admin|administrator|team|staff)\b/,
    )
  ) {
    triggered.add("userRoles");
  }

  if (
    hasPattern(
      text,
      /\b(dashboard|list|lists|table|records|record|tracker|tracking|statuses|status|reporting|reports|filtering|filter|filters|search|searching)\b/,
    )
  ) {
    triggered.add("tracking");
  }

  if (isVagueIdea(answers)) {
    triggered.add("appType");
  }

  return triggerPriority
    .filter((id) => triggered.has(id))
    .slice(0, 3)
    .map((id) => guidedFollowUps.find((followUp) => followUp.id === id))
    .filter((followUp): followUp is GuidedFollowUp => Boolean(followUp));
}

export function pruneSmartFollowUpAnswers(
  answers: GuidedFollowUpAnswers,
  activeFollowUps: GuidedFollowUp[],
): GuidedFollowUpAnswers {
  const activeIds = new Set(activeFollowUps.map((followUp) => followUp.id));

  return Object.fromEntries(
    Object.entries(answers).filter(([id]) =>
      activeIds.has(id as GuidedFollowUpId),
    ),
  ) as GuidedFollowUpAnswers;
}

export function getFollowUpById(id: GuidedFollowUpId): GuidedFollowUp | null {
  return guidedFollowUps.find((followUp) => followUp.id === id) ?? null;
}
